/* Compact quick-add: lightweight variant chooser (2026-10-05)
 * Replaces Dawn's full product-info injection with a minimal JSON-based UI.
 * Fetches /products/{handle}.js and renders: image, title, price, variant radios, Add to cart.
 */
if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');
        const cleanup = () => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        };

        // Open immediately with loading state
        this.modalContent.innerHTML =
          '<div class="quick-add-compact"><div class="quick-add-compact__loading"><div class="loading__spinner"></div></div></div>';
        try {
          super.show(opener);
        } catch (e) {
          this.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        }

        fetch(`${productUrl}.js`)
          .then((r) => {
            if (!r.ok) throw new Error('product json failed');
            return r.json();
          })
          .then((product) => {
            this.modalContent.innerHTML = this.renderCompact(product, productUrl);
            this.wireUp(product);
          })
          .catch(() => {
            this.modalContent.innerHTML =
              '<div class="quick-add-compact"><p class="quick-add-compact__error">Could not load options. <a href="' +
              productUrl +
              '">View full product page</a></p></div>';
          })
          .finally(cleanup);
      }

      renderCompact(product, productUrl) {
        const money = (cents) => '$' + (cents / 100).toFixed(2) + ' CAD';
        const img = product.featured_image
          ? '<img src="' + product.featured_image + '" alt="" class="quick-add-compact__image" loading="lazy">'
          : '';
        let optionsHtml = '';
        if (product.variants.length > 1 && product.options) {
          optionsHtml = product.options
            .map((opt, optIdx) => {
              const optName = typeof opt === 'string' ? opt : opt.name;
              const values = [...new Set(product.variants.map((v) => v['option' + (optIdx + 1)]))];
              const radios = values
                .map(
                  (val, i) =>
                    '<label class="quick-add-compact__option"><input type="radio" name="qa-opt-' +
                    optIdx +
                    '" value="' +
                    val.replace(/"/g, '&quot;') +
                    '"' +
                    (i === 0 ? ' checked' : '') +
                    '><span>' +
                    val +
                    '</span></label>'
                )
                .join('');
              return (
                '<fieldset class="quick-add-compact__fieldset"><legend>' +
                optName +
                '</legend><div class="quick-add-compact__options">' +
                radios +
                '</div></fieldset>'
              );
            })
            .join('');
        }
        return (
          '<div class="quick-add-compact">' +
          img +
          '<h3 class="quick-add-compact__title">' +
          product.title +
          '</h3>' +
          '<div class="quick-add-compact__price">' +
          money(product.price_min || product.price) +
          '</div>' +
          optionsHtml +
          '<button type="button" class="quick-add-compact__submit button button--full-width button--primary">Add to cart</button>' +
          '<a href="' +
          productUrl +
          '" class="quick-add-compact__details">View full details</a>' +
          '</div>'
        );
      }

      wireUp(product) {
        const submit = this.modalContent.querySelector('.quick-add-compact__submit');
        if (!submit) return;
        submit.addEventListener('click', () => {
          const selected = {};
          product.options?.forEach((_, optIdx) => {
            const checked = this.modalContent.querySelector('input[name="qa-opt-' + optIdx + '"]:checked');
            if (checked) selected['option' + (optIdx + 1)] = checked.value;
          });
          let variant = product.variants[0];
          if (product.variants.length > 1) {
            variant =
              product.variants.find((v) =>
                Object.keys(selected).every((k) => v[k] === selected[k])
              ) || variant;
          }
          submit.setAttribute('disabled', '');
          submit.textContent = 'Adding...';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: variant.id,
              quantity: 1,
              sections: ['cart-drawer', 'cart-icon-bubble'],
            }),
          })
            .then((r) => {
              if (!r.ok) throw new Error('add failed');
              return r.json();
            })
            .then((parsedState) => {
              this.hide(true);
              if (window.updateCartBubble) window.updateCartBubble();
              if (window.syncInCartButtons) setTimeout(window.syncInCartButtons, 500);
              this.openCartDrawer(parsedState);
            })
            .catch(() => {
              submit.removeAttribute('disabled');
              submit.textContent = 'Add to cart';
              const err = document.createElement('p');
              err.className = 'quick-add-compact__error';
              err.textContent = 'Could not add to cart. Please try again.';
              this.modalContent.querySelector('.quick-add-compact').appendChild(err);
            });
        });
      }

      openCartDrawer(parsedState) {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) {
          window.location.href = '/cart';
          return;
        }
        // Use Dawn's native renderContents (re-binds overlay + close, updates sections)
        if (parsedState && parsedState.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(parsedState);
        } else if (typeof drawer.open === 'function') {
          drawer.open();
        } else {
          window.location.href = '/cart';
        }
      }
    }
  );
}

/* Dynamic cart count bubble (2026-10-06) */
(function updateCartBubble() {
  window.updateCartBubble = function() {
    fetch('/cart.js').then(r => r.json()).then(cart => {
      const link = document.getElementById('cart-icon-bubble');
      if (!link) return;
      let bubble = link.querySelector('.cart-count-bubble');
      const count = cart.item_count;
      if (count > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          link.appendChild(bubble);
        }
        bubble.innerHTML =
          '<span aria-hidden="true">' + (count < 100 ? count : '') + '</span>' +
          '<span class="visually-hidden">Cart, ' + count + ' items</span>';
        bubble.style.display = '';
      } else if (bubble) {
        bubble.remove();
      }
    }).catch(() => {});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', window.updateCartBubble);
  else window.updateCartBubble();
  // Re-sync when cart drawer opens/closes (native adds happen then)
  document.addEventListener('click', (e) => {
    if (e.target.closest('#cart-icon-bubble, cart-drawer')) {
      setTimeout(window.updateCartBubble, 800);
    }
  });
})();

/* In-cart button state with live sync (2026-10-06)
 * Server always renders functional buttons. JS disables those in cart,
 * storing original HTML for perfect restore on removal. */
(function inCartSync() {
  function sync() {
    fetch('/cart.js').then(r => r.json()).then(cart => {
      const inCart = new Set(cart.items.map(i => String(i.product_id)));
      document.querySelectorAll('.card-wrapper').forEach(card => {
        const btn = card.querySelector('.quick-add__submit');
        if (!btn || !btn.dataset.productId) return;
        const pid = btn.dataset.productId;
        const shouldDisable = inCart.has(pid);
        const isDisabled = btn.hasAttribute('disabled');

        if (shouldDisable && !isDisabled) {
          if (!btn.dataset.originalInner) btn.dataset.originalInner = btn.innerHTML;
          btn.setAttribute('disabled', '');
          btn.setAttribute('aria-disabled', 'true');
          btn.innerHTML = '&#10003; In cart';
        } else if (!shouldDisable && isDisabled && btn.dataset.originalInner) {
          // Restore in place (do NOT replace element — preserves modal-opener listeners)
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          btn.innerHTML = btn.dataset.originalInner;
          delete btn.dataset.originalInner;
        }
      });
    }).catch(() => {});
  }
  window.syncInCartButtons = sync;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
  // Re-sync after cart changes (drawer interactions)
  document.addEventListener('click', (e) => {
    if (e.target.closest('#cart-icon-bubble, cart-drawer, .quick-add-compact__submit, .quick-add__submit')) {
      setTimeout(sync, 1000);
    }
  });
})();
